import { useDispatch, useSelector } from "react-redux";
import styles from "./index.module.css";
import { useCallback, useEffect, useMemo, useState } from "react";
import { setNotes } from "../../../store/slices/popup/popup.slice";
import { NavLink } from "react-router-dom";
import { locationMapping, rowCounts } from "../../../utils/constant";
import Modal from "../Modal";
import {
  addProductWatchlistAsyncThunk,
  deleteProductAsyncThunk,
  fetchWatchLists,
  getAllProductsIdsAsyncThunk,
} from "../../../store/slices/watchlist/data.slice";
import DropDown from "../Dropdown";
import { convert_usd_to_nzd, formatPriceRange, getCrossRateLabel, getNativeCurrencyForWebsite, getPriceWithShelf, getUsdPerLocalRate } from "../../../utils/conversion";
import {
  expandPromosForDisplay,
  getEffectiveDefaultPriceUsd,
  getPromoDisplayText,
  getPromoPriceUsd,
  promosHaveDisplayablePrices,
} from "../../../utils/promo";
import PulseLoader from "react-spinners/PulseLoader";
import { WiStars } from "react-icons/wi";
import { fetchAISummaryFromData } from "../../../store/slices/ai/ai.thunk";
import { fetchAISummaryFromDataAsyncThunk } from "../../../store/slices/ai/ai.slice";
import { TypeAnimation } from "react-type-animation";
import PriceWithShelf from "../PriceWithShelf";

const DfProductCard = (props) => {
  const [expand, setExpand] = useState(false);
  const [format, setFormat] = useState("");
  const [toggleWatchlist, setToggleWatchlist] = useState(false);
  const {
    watchLists,
    loading: watchlistLoading,
    error,
    products,
  } = useSelector((store) => store.watchlist);
  const [watchlistState, setWatchlistState] = useState([]);
  const dispatch = useDispatch();

  const toggleExpand = () => setExpand((state) => !state);

  const enableNotes = (e) => {
    dispatch(setNotes({ showNotes: true, notes: e?.target?.dataset?.notes }));
  };
  const [promotionModal, setPromotionModal] = useState({
    show: false,
    promos: [],
    defaultPrice: "",
    defaultPriceUsd: null,
    rateSource: null,
    website: null,
  });

  const [summaryModal, setSummaryModal] = useState(false);
  const { loadingProductSummary, productSummary } = useSelector(
    (store) => store.ai
  );
  const { source, currency } = props;
  // Converts the price of product to per unit value.
  function getPricePerUnit(quantity, unit, price) {
    const conversionFactors = {
      l: 1000,
      ml: 1,
      g: 1,
      gm: 1,
      kg: 1000,
    };
    const convertUnit = {
      l: "ml",
      ml: "ml",
      g: "gm",
      gm: "gm",
      kg: "gm",
    };

    // Check if the unit is valid
    if (!conversionFactors[unit]) {
      return "-";
    }

    // Convert quantity to the base unit
    const normalizedQuantity = quantity * conversionFactors[unit];

    // Calculate price per unit (base unit is ml for liquids and g for weight)
    const pricePerUnit = price / normalizedQuantity;

    return pricePerUnit.toFixed(3) + " ($/" + convertUnit[unit] + ")";
  }

  const getConvertedPrice = useCallback(
    (value, rateSource) => {
      const rates =
        rateSource?.conversion_rate_nzd != null ? rateSource : undefined;
      return convert_usd_to_nzd(value, currency, rates);
    },
    [currency]
  );

  const getPromoRateSource = (promo, rateSource) =>
    promo?.conversion_rate_nzd != null ? promo : rateSource;

  // Helper function to deduplicate promos based on text
  const deduplicatePromos = (promos) => {
    if (!promos || !Array.isArray(promos)) return promos;
    
    const seen = new Set();
    return promos.filter(promo => {
      if (!promo || !promo.text) return false;
      
      const text = promo.text.toLowerCase().trim();
      if (seen.has(text)) {
        return false; // Skip this duplicate
      }
      
      seen.add(text);
      return true; // Keep this unique entry
    });
  };

  const getPromosPriceDisplay = useCallback(
    (promos, defaultPriceUsd, rateSource, website) => {
      const nativeCurrency = getNativeCurrencyForWebsite(website);
      const displayPromos = expandPromosForDisplay(
        promos,
        (cur) => getUsdPerLocalRate(cur, rateSource),
        nativeCurrency
      );
      const effectiveDefaultUsd = getEffectiveDefaultPriceUsd(
        displayPromos,
        defaultPriceUsd
      );

      const usdPromoPairs = displayPromos
        .map((item) => {
          const usd = getPromoPriceUsd(item, defaultPriceUsd);
          if (usd == null || Number.isNaN(usd)) return null;
          return { usd, rateSource: getPromoRateSource(item, rateSource) };
        })
        .filter(Boolean);

      usdPromoPairs.push({ usd: effectiveDefaultUsd, rateSource });

      const displayPrices = usdPromoPairs.map(({ usd, rateSource: rs }) =>
        parseFloat(getConvertedPrice(usd, rs))
      );
      const shelfPrices = usdPromoPairs.map(({ usd, rateSource: rs }) => {
        const rates = rs?.conversion_rate_nzd != null ? rs : undefined;
        return parseFloat(convert_usd_to_nzd(usd, nativeCurrency, rates));
      });

      const showShelf =
        nativeCurrency !== currency &&
        displayPrices.every((p) => !Number.isNaN(p)) &&
        shelfPrices.every((p) => !Number.isNaN(p));
      const rateSourceForFx = usdPromoPairs[0]?.rateSource || rateSource;

      return {
        display: formatPriceRange(displayPrices),
        shelf: formatPriceRange(shelfPrices),
        nativeCurrency,
        showShelf,
        fxLabel: showShelf
          ? getCrossRateLabel(nativeCurrency, currency, rateSourceForFx)
          : null,
      };
    },
    [currency, getConvertedPrice]
  );

  const getPromoDisplayWithShelf = useCallback(
    (promo, defaultPriceUsd, rateSource, website) => {
      const promoPriceUsd = getPromoPriceUsd(promo, defaultPriceUsd);
      if (promoPriceUsd == null || Number.isNaN(promoPriceUsd)) {
        return {
          display: "-",
          shelf: "-",
          nativeCurrency: "NZD",
          showShelf: false,
          fxLabel: null,
        };
      }
      const promoRateSource = getPromoRateSource(promo, rateSource);
      return getPriceWithShelf(
        promoPriceUsd,
        currency,
        website,
        promoRateSource
      );
    },
    [currency]
  );

  const getEffectiveDefaultDisplayWithShelf = useCallback(
    (promos, defaultPriceUsd, rateSource, website) =>
      getPriceWithShelf(
        getEffectiveDefaultPriceUsd(promos, defaultPriceUsd),
        currency,
        website,
        rateSource
      ),
    [currency]
  );

  useEffect(() => {
    const brand = ["Aelia", "Lotte", "Heinemann", "Auckland dutyfree"];
    const {
      canprod_id,
      products_data,
      source_price,
      source_pricerank,
      difference,
      difference_percentage,
      average,
      source_website = source,
    } = props;
    
    // Safety check: ensure products_data exists and has items
    if (!products_data || !Array.isArray(products_data) || products_data.length === 0) {
      setFormat({
        imageURL: "",
        title: "No product data available",
        sku: "",
        brand: "",
        average: 0,
        difference: 0,
        difference_percentage: 0,
        averageDisplay: "0.00",
        differenceDisplay: "0.00",
        rateSource: null,
        product_id: "",
        canprod_id: "",
        subProducts: [],
        source: source_website,
        priceRange: { minPrice: 0, maxPrice: 0 },
        isPromotion: false,
        priceRank: ""
      });
      return;
    }
    
    const data = {};

    const aeliaAucklandIndex = products_data
      .map((item) => item.website)
      .indexOf(source_website);
    
    // Safety check: if source doesn't exist in products_data, use the first available product
    const sourceProduct = aeliaAucklandIndex !== -1 
      ? products_data[aeliaAucklandIndex] 
      : products_data[0];
    
    if (!sourceProduct) {
      // If no products available, set default values
      data.imageURL = "";
      data.title = "Product not available";
      data.sku = "";
      data.brand = "";
      data.average = Number(average);
      data.difference = Number(difference);
      data.difference_percentage = difference_percentage;
      data.product_id = "";
      data.rateSource = null;
    } else {
      data.imageURL = sourceProduct.image_url;
      data.title = sourceProduct.title;
      data.sku = sourceProduct.sku != null && String(sourceProduct.sku).trim() !== "" ? String(sourceProduct.sku).trim() : "";
      const sourceLocationInfo = locationMapping[source_website] || "";
      data.brand = sourceProduct.brand || sourceLocationInfo.brand || "";
      data.average = Number(average);
      data.difference = Number(difference);
      data.difference_percentage = difference_percentage;
      data.product_id = sourceProduct.product_id;
      data.rateSource = sourceProduct;
    }
    data.canprod_id = canprod_id;
    data.subProducts = [];
    data.source = source_website;
    let minLocalPrice = Number.MAX_VALUE;
    let maxLocalPrice = Number.MIN_VALUE;
    let isPromotion = false;
    const competitorLocalPrices = [];
    let sourceLocalPrice = null;

    products_data.map((product) => {
      const localPrice = parseFloat(
        getConvertedPrice(Number(product.latest_price), product)
      );
      if (!Number.isNaN(localPrice)) {
        minLocalPrice = Math.min(minLocalPrice, localPrice);
        maxLocalPrice = Math.max(maxLocalPrice, localPrice);
        if (product.website === source_website) {
          sourceLocalPrice = localPrice;
        } else {
          competitorLocalPrices.push(localPrice);
        }
      }
      if (
        promosHaveDisplayablePrices(
          product.latest_promotions,
          Number(product.latest_price)
        )
      ) {
        isPromotion = true;
      }
      // Safety check for locationMapping
      const locationInfo = locationMapping[product.website] || { location: "Unknown", brand: "Unknown", type: "Domestic" };
      
      let productType = "DOMESTIC";
      if (product?.tag) {
        productType = product.tag.toLowerCase() === "duty-free" ? "DUTY-FREE" : "DOMESTIC";
      } else if (locationInfo?.type) {
        productType = locationInfo.type.toLowerCase().includes("duty") ? "DUTY-FREE" : "DOMESTIC";
      } else if (brand.includes(locationInfo.brand)) {
        productType = "DUTY-FREE";
      }
      
      let temp = {
        location: locationInfo.location,
        brand: locationInfo.brand,
        price: product.latest_price,
        quantity: product.qty,
        unit: product.unit,
        url: product.url,
        type: productType,
        image: product.image_url,
        website: product.website,
        pricerank: product.pricerank,
        promos: deduplicatePromos(product.latest_promotions),
        hasPromoPrices: promosHaveDisplayablePrices(
          product.latest_promotions,
          Number(product.latest_price)
        ),
        priceperunit: product.unit
          ? getPricePerUnit(
              Number(product.qty),
              product.unit,
              Number(product.latest_price)
            )
          : product.latest_price,
        id: product.id,
        conversion_rate_nzd: product.conversion_rate_nzd,
        conversion_rate_aud: product.conversion_rate_aud,
        conversion_rate_sgd: product.conversion_rate_sgd,
      };
      data.subProducts.push(temp);
    });

    // Sorting the sub products data.
    data.subProducts.sort((a, b) => {
      // Safety check for pricerank format
      if (!a.pricerank || !b.pricerank) return 0;
      
      const [numA, denA] = a.pricerank.split("/").map(Number);
      const [numB, denB] = b.pricerank.split("/").map(Number);
      
      // Check if the split resulted in valid numbers
      if (isNaN(numA) || isNaN(numB)) return 0;
      
      return numA - numB;
    });

    if (minLocalPrice === Number.MAX_VALUE || maxLocalPrice === Number.MIN_VALUE) {
      data.priceRange = {
        minPrice: 0,
        maxPrice: 0,
      };
    } else {
      data.priceRange = {
        minPrice: minLocalPrice,
        maxPrice: maxLocalPrice,
      };
    }

    if (competitorLocalPrices.length > 0) {
      const localAverage =
        competitorLocalPrices.reduce((sum, p) => sum + p, 0) /
        competitorLocalPrices.length;
      data.averageDisplay = localAverage.toFixed(2);
      if (sourceLocalPrice != null) {
        data.differenceDisplay = (sourceLocalPrice - localAverage).toFixed(2);
      } else {
        data.differenceDisplay = getConvertedPrice(
          Number(difference),
          data.rateSource
        );
      }
    } else {
      data.averageDisplay = getConvertedPrice(Number(average), data.rateSource);
      data.differenceDisplay = getConvertedPrice(
        Number(difference),
        data.rateSource
      );
    }

    data.isPromotion = isPromotion;
    data.priceRank = source_pricerank;
    setFormat(data);
  }, [props, getConvertedPrice]);

  useEffect(() => {
    if (toggleWatchlist) {
      watchLists.forEach((item) => {
        dispatch(
          getAllProductsIdsAsyncThunk({
            watchlist_id: item.watchlist_id,
            source,
          })
        );
      });
    }
  }, [toggleWatchlist]);

  // Add product to watchlist
  function addProduct(watchlist_id, product_id) {
    dispatch(
      addProductWatchlistAsyncThunk({
        watchlist_id,
        product_id,
        source,
      })
    );
  }

  function handleRemoveProductFromWatchlist(watchlist_id, product_id) {
    setWatchlistState(watchlistState.filter((item) => item !== watchlist_id));
    dispatch(
      deleteProductAsyncThunk({
        watchlist_id,
        product_id,
        source,
      })
    );
  }

  const openPromotionModal = (promos, defaultPrice, defaultPriceUsd, rateSource, website) => {
    setPromotionModal({
      show: true,
      promos: [...deduplicatePromos(promos)].reverse(),
      defaultPrice,
      defaultPriceUsd,
      rateSource,
      website,
    });
  };

  const toggleSummaryModal = () => {
    setSummaryModal((st) => !st);
    if (!summaryModal) {
      dispatch(
        fetchAISummaryFromDataAsyncThunk({
          data: { product: format, type: "PRODUCT" },
        })
      );
    }
  };
  return (
    format && (
      <div className={styles["wrapper"]}>
        <div className={styles["container"]}>
          <div className={styles["content"]}>
            <div>
              <img src={format.imageURL} alt={format.title} />
            </div>
            <div>
              <div className={styles["title"]}>
                {format.title}
                {format.sku ? ` (SKU : ${format.sku})` : ""}
              </div>
              <div className={styles["store"]}>
                {format.brand} {format.isPromotion && <span>promo</span>}
              </div>
            </div>
            <div>
              <div className={styles["label"]}>Price Range</div>
              <div className={styles["value"]}>
                {Number(format.priceRange.minPrice).toFixed(2)} -{" "}
                {Number(format.priceRange.maxPrice).toFixed(2)}
              </div>
            </div>
            <div>
              <div className={styles["label"]}>Price Rank</div>
              <div className={styles["value"]}>
                {format.priceRank} / {format.subProducts.length}
              </div>
            </div>
            <div>
              <div className={styles["label"]}>Average</div>
              <div className={styles["value"]}>
                {format.averageDisplay ??
                  getConvertedPrice(format.average, format.rateSource)}
              </div>
            </div>
            <div>
              <div className={styles["label"]}>Difference</div>
              <div className={styles["value"]}>
                {format.differenceDisplay ??
                  getConvertedPrice(format.difference, format.rateSource)}
                <small style={{ fontSize: "1.4rem", marginLeft: "0.7rem" }}>
                  {"(" + format.difference_percentage + "%)"}
                </small>
              </div>
            </div>
            {/* <div>
              <WiStars
                title="AI summary"
                className={styles["bulb-icon"]}
                onClick={toggleSummaryModal}
              />
            </div> */}
            <NavLink
              to={`/df-price-history/${format.canprod_id}`}
              state={{
                imgURL: format.imageURL,
                title: format.title,
                brand: format.brand,
              }}
              className={styles["price-history"]}
            >
              Price History
              <i class="bi bi-box-arrow-up-right"></i>
            </NavLink>
            <div
              className={
                !expand
                  ? styles["breakdown"]
                  : styles["breakdown"] + " " + styles["inverse"]
              }
              onClick={toggleExpand}
            >
              Breakdown
              <i class="bi bi-chevron-down"></i>
            </div>
            <div className={styles["add-to-watchlist"]}>
              {props?.type == "watchlist" ? (
                <i
                  class="bi bi-trash"
                  onClick={() =>
                    props.deleteProduct(format.product_id, format.source)
                  }
                ></i>
              ) : (
                <i
                  class="bi bi-plus-square"
                  onClick={() => {
                    setToggleWatchlist(true);
                    dispatch(fetchWatchLists());
                  }}
                ></i>
              )}
            </div>
          </div>
        </div>
        {expand && (
          <div className={styles["details"]}>
            {format.subProducts &&
              format.subProducts.map((detail) => {
                const hasPromoPrices =
                  detail?.promos?.length > 0 &&
                  promosHaveDisplayablePrices(
                    detail?.promos,
                    Number(detail?.price)
                  );
                const priceDisplay = hasPromoPrices
                  ? getPromosPriceDisplay(
                      detail.promos,
                      Number(detail.price),
                      detail,
                      detail.website
                    )
                  : getPriceWithShelf(
                      Number(detail.price),
                      currency,
                      detail.website,
                      detail
                    );

                return (
                  <div
                    key={detail.id || detail.website}
                    className={
                      styles["detail"] +
                      " " +
                      (detail?.website == source ? styles["self"] : "")
                    }
                  >
                    <div>{detail?.location.toUpperCase()}</div>
                    <div>
                      <a
                        href={detail?.url}
                        target="_blank"
                        rel="noreferrer"
                        style={{ color: "black" }}
                      >
                        {detail?.brand.toUpperCase()}
                      </a>
                    </div>
                    <div
                      className={
                        hasPromoPrices ? styles["underline"] : ""
                      }
                      onClick={() => {
                        if (hasPromoPrices) {
                          openPromotionModal(
                            detail?.promos,
                            priceDisplay.display,
                            Number(detail?.price),
                            detail,
                            detail.website
                          );
                        }
                      }}
                    >
                      <PriceWithShelf {...priceDisplay} hasPromo={hasPromoPrices} />
                    </div>
                    <div>
                      {detail?.quantity}
                      {detail?.unit || "-"}
                    </div>
                    <div>
                      {getConvertedPrice(
                        detail?.priceperunit.replace("($/ml)", ""),
                        detail
                      ) + (detail.unit ? " ($/ml)" : "")}
                    </div>
                    <div>{detail?.type}</div>
                  </div>
                );
              })}
          </div>
        )}

        {/* Select Watchlist Modal */}
        {toggleWatchlist && (
          <Modal>
            <div className={styles["selectWatchlist"]}>
              <div className={styles["heading"]}>
                <h3>Select Watchlist</h3>
                <span>Add product to watchlist</span>
              </div>

                <ul className={styles["watchlists"]}>
                  {watchlistLoading ? (
                    <div className="loader-container">
                      <PulseLoader />
                    </div>
                  ) : (
                    watchLists.map((item, index) => (
                      <li>
                        <span>{item.watchlist_name}</span>
                        {products[item.watchlist_id]?.includes(
                          format.product_id
                        ) ||
                        watchlistState.includes(Number(item.watchlist_id)) ? (
                          <i
                            class="bi bi-dash-square-fill"
                            style={{
                              color: "#028d7f",
                            }}
                            onClick={() =>
                              handleRemoveProductFromWatchlist(
                                item.watchlist_id,
                                format.product_id
                              )
                            }
                          ></i>
                        ) : (
                          <i
                            class="bi bi-plus-square"
                            onClick={() => {
                              addProduct(item.watchlist_id, format.product_id);
                              setWatchlistState([
                                ...watchlistState,
                                Number(item.watchlist_id),
                              ]);
                            }}
                          ></i>
                        )}
                      </li>
                    ))
                  )}
                </ul>
              <div
                className={styles["done"]}
                onClick={() => setToggleWatchlist(false)}
              >
                Done
              </div>
            </div>
          </Modal>
        )}

        {/* Promotion Modal */}
        {promotionModal.show && (
          <Modal>
            <div className={styles["promotionModal"]}>
              <div className={styles["heading"]}>
                <h3>Promos</h3>
                <span>Showing promos for {format.brand}</span>
              </div>
              <div className={styles["promos"]}>
                <div className={styles["section"]}>
                  <h3>Promo</h3>
                  <div className={styles["promo-items"]}>
                    {expandPromosForDisplay(
                      promotionModal.promos,
                      (cur) =>
                        getUsdPerLocalRate(cur, promotionModal.rateSource),
                      getNativeCurrencyForWebsite(promotionModal.website)
                    ).map((promo, index) => {
                      const promoPrice = getPromoDisplayWithShelf(
                        promo,
                        promotionModal.defaultPriceUsd,
                        promotionModal.rateSource,
                        promotionModal.website
                      );
                      return (
                      <div key={index} className={styles["promo-item"]}>
                        <span className={styles["promo-text"]}>
                          {getPromoDisplayText(promo).toUpperCase()}
                        </span>
                        <span className={styles["promo-price"]}>
                          <PriceWithShelf
                            {...promoPrice}
                            prefix="$"
                            hasPromo={false}
                          />
                        </span>
                      </div>
                      );
                    })}
                    {(() => {
                      const defaultPrice = getEffectiveDefaultDisplayWithShelf(
                        promotionModal.promos,
                        promotionModal.defaultPriceUsd,
                        promotionModal.rateSource,
                        promotionModal.website
                      );
                      return (
                    <div className={styles["promo-item"]}>
                      <span className={styles["promo-text"]}>DEFAULT</span>
                      <span className={styles["promo-price"]}>
                        <PriceWithShelf
                          {...defaultPrice}
                          prefix="$"
                          hasPromo={false}
                        />
                      </span>
                    </div>
                      );
                    })()}
                  </div>
                </div>
              </div>
              <div
                className={styles["done"]}
                onClick={() =>
                  setPromotionModal({
                    show: false,
                    promos: [],
                    defaultPrice: "",
                    defaultPriceUsd: null,
                    rateSource: null,
                    website: null,
                  })
                }
              >
                Done
              </div>
            </div>
          </Modal>
        )}

        {/*Summary modal */}
        {summaryModal && (
          <Modal>
            <div className={styles["modal-container"]}>
              <h2 className={styles["modal-title"]}>AI Summary</h2>
              <div className={styles["modal-content"]}>
                {loadingProductSummary ? (
                  <PulseLoader size={8} />
                ) : (
                  <TypeAnimation
                    style={{
                      whiteSpace: "pre-line",
                      display: "block",
                      fontSize: "1.6rem",
                      lineHeight: "1.4",
                      color: "#333",
                    }}
                    sequence={[productSummary]}
                    speed={99}
                    cursor={false}
                  />
                )}
              </div>
              <button
                className={styles["modal-close"]}
                onClick={toggleSummaryModal}
              >
                Close
              </button>
            </div>
          </Modal>
        )}
      </div>
    )
  );
};

export default DfProductCard;
